import { companyApi, type CompanyImageVo } from '@/axios/company';
import AccessButton from '@/components/AccessButton';
import CommonModalFactory from '@/plugins/ModalFactory';
import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Button, Input, message, Progress, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { useEffect, useMemo, useState } from 'react';
import type { UIEvent } from 'react';
import styles from './CompanyImageGallery.module.less';

const ACCEPT_IMAGE_TYPES = '.jpg,.jpeg,.png';
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const MAX_IMAGE_COUNT = 100;
const IMAGE_RENDER_BATCH_SIZE = 20;

type UploadTask = {
  uid: string;
  companyId: string;
  name: string;
  previewUrl: string;
  percent: number;
  status: 'uploading' | 'done' | 'error';
};

let uploadTasks: UploadTask[] = [];
const listeners = new Set<() => void>();

const notifyUploadTasks = () => listeners.forEach((listener) => listener());

const subscribeUploadTasks = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const updateTask = (uid: string, patch: Partial<UploadTask>) => {
  uploadTasks = uploadTasks.map((task) => (task.uid === uid ? { ...task, ...patch } : task));
  notifyUploadTasks();
};

const removeTask = (uid: string) => {
  uploadTasks = uploadTasks.filter((task) => task.uid !== uid);
  notifyUploadTasks();
};

const isSupportedImage = (file: File) => {
  const ext = file.name.split('.').pop()?.toLowerCase();
  return !!ext && ['jpg', 'jpeg', 'png'].includes(ext);
};

const getUploadingTaskCount = (companyId: string) =>
  uploadTasks.filter((task) => task.companyId === companyId && task.status === 'uploading').length;

const startUpload = async (companyId: string, file: File) => {
  const uid = `${Date.now()}-${file.name}-${Math.random()}`;
  const task: UploadTask = {
    uid,
    companyId,
    name: file.name,
    previewUrl: URL.createObjectURL(file),
    percent: 0,
    status: 'uploading',
  };
  uploadTasks = [task, ...uploadTasks];
  notifyUploadTasks();
  try {
    await companyApi.uploadImage({
      companyId,
      file,
      onUploadProgress: (event) => {
        if (!event.total) {
          return;
        }
        updateTask(uid, { percent: Math.min(99, Math.round((event.loaded / event.total) * 100)) });
      },
    });
    updateTask(uid, { percent: 100, status: 'done' });
  } catch {
    updateTask(uid, { status: 'error' });
  } finally {
    removeTask(uid);
  }
};

const useCompanyUploadTasks = (companyId: string) => {
  const [version, setVersion] = useState(0);

  useEffect(() => subscribeUploadTasks(() => setVersion((value) => value + 1)), []);

  return useMemo(
    () => uploadTasks.filter((task) => task.companyId === companyId && task.status === 'uploading'),
    [companyId, version]
  );
};

const ImageCard = ({
  image,
  companyId,
  editable,
  onChanged,
}: {
  image: CompanyImageVo;
  companyId: string;
  editable?: boolean;
  onChanged: () => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(image.imageName);

  useEffect(() => {
    setName(image.imageName);
  }, [image.imageName]);

  const saveName = async () => {
    const nextName = name.trim();
    if (!nextName) {
      message.warning('图片名称不能为空');
      setName(image.imageName);
      setEditing(false);
      return;
    }
    if (nextName === image.imageName) {
      setEditing(false);
      setName(image.imageName);
      return;
    }
    await companyApi.updateImage({ companyId, imageId: image.imageId, imageName: nextName });
    setEditing(false);
    onChanged();
  };

  const removeImage = async () => {
    await companyApi.deleteImage({ companyId, imageId: image.imageId });
    message.success('删除成功');
    onChanged();
  };

  return (
    <div className={editable ? styles.modalCard : styles.imageCard}>
      <img src={image.thumbnailUrl || image.imageUrl} alt={image.imageName} loading="lazy" decoding="async" />
      <Button
        className={styles.deleteBtn}
        shape="circle"
        size="small"
        danger
        icon={<DeleteOutlined />}
        onClick={removeImage}
      />
      {editing ? (
        <Input
          className={styles.modalNameInput}
          size="small"
          value={name}
          autoFocus
          onChange={(event) => setName(event.target.value)}
          onBlur={saveName}
          onPressEnter={saveName}
        />
      ) : (
        <div
          className={editable ? styles.modalName : styles.imageName}
          title={image.imageName}
          onDoubleClick={() => setEditing(true)}
        >
          {image.imageName}
        </div>
      )}
    </div>
  );
};

export const openCompanyImageModal = CommonModalFactory<{
  companyId: string;
  images: CompanyImageVo[];
  refresh: () => void;
}>(
  {
    title: '编辑图片',
    width: 860,
    okText: '确定',
    cancelText: '取消',
    maskClosable: false,
  },
  ({ data }) => {
    const uploadingTasks = useCompanyUploadTasks(data.companyId);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [visibleCount, setVisibleCount] = useState(IMAGE_RENDER_BATCH_SIZE);
    const { data: images = data.images, run: refreshImages } = useRequest(
      () => companyApi.listImages(data.companyId),
      {
        ready: !!data.companyId,
      }
    );

    const refreshAll = () => {
      refreshImages();
      data.refresh();
    };
    const visibleImages = useMemo(() => images.slice(0, visibleCount), [images, visibleCount]);
    const hasMoreImages = visibleCount < images.length;
    const loadMoreImages = () => {
      setVisibleCount((count) => Math.min(count + IMAGE_RENDER_BATCH_SIZE, images.length));
    };
    const handleImageListScroll = (event: UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      if (hasMoreImages && target.scrollHeight - target.scrollTop - target.clientHeight < 24) {
        loadMoreImages();
      }
    };

    const beforeUpload = (file: File) => {
      if (images.length + getUploadingTaskCount(data.companyId) >= MAX_IMAGE_COUNT) {
        message.warning({
          key: 'company-image-count-limit',
          content: `企业图片最多上传${MAX_IMAGE_COUNT}张`,
        });
        return Upload.LIST_IGNORE;
      }
      if (!isSupportedImage(file)) {
        message.warning('仅支持 JPG、JPEG、PNG 格式图片');
        return Upload.LIST_IGNORE;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        message.warning('单张图片大小不能超过10MB');
        return Upload.LIST_IGNORE;
      }
      void startUpload(data.companyId, file).finally(refreshAll);
      return false;
    };

    return (
      <div>
        <div className={styles.modalUpload}>
          <Upload.Dragger
            multiple
            accept={ACCEPT_IMAGE_TYPES}
            fileList={fileList}
            beforeUpload={beforeUpload}
            onChange={({ fileList: nextFileList }) => setFileList(nextFileList)}
            showUploadList={false}
            className={styles.uploadDragger}
          >
            <p>
              <InboxOutlined style={{ color: '#a8afbd', fontSize: 38 }} />
            </p>
            <Button type="primary">点击上传</Button>
            <div style={{ color: '#98a1b2', marginTop: 8 }}>或将图片拖拽到此处上传</div>
          </Upload.Dragger>
          <div className={styles.uploadHint}>
            <div>• 支持上传的格式：JPG、JPEG、PNG</div>
            <div>• 单张图片大小 &lt;= 10MB</div>
          </div>
        </div>
        <div className={styles.modalHeader}>
          <div>
            已上传图片
            <span className={styles.modalTip}>上传过程中可切换页面，任务不中断</span>
          </div>
          <span>
            {images.length + uploadingTasks.length}/{MAX_IMAGE_COUNT}
          </span>
        </div>
        <div className={styles.modalList} onScroll={handleImageListScroll}>
          <div className={styles.modalGrid}>
            {visibleImages.map((image) => (
              <ImageCard
                key={image.imageId}
                image={image}
                companyId={data.companyId}
                editable
                onChanged={refreshAll}
              />
            ))}
            {uploadingTasks.map((task) => (
              <div className={styles.modalCard} key={task.uid}>
                <img src={task.previewUrl} alt={task.name} />
                <div className={styles.uploadMask}>
                  <span>上传中 {task.percent}%</span>
                  <Progress percent={task.percent} size="small" showInfo={false} strokeColor="#ff7a1a" />
                </div>
              </div>
            ))}
          </div>
          {hasMoreImages && (
            <Button block type="link" className={styles.loadMoreBtn} onClick={loadMoreImages}>
              查看更多
            </Button>
          )}
        </div>
      </div>
    );
  }
);

const CompanyImageGallery = ({ companyId }: { companyId: string }) => {
  const uploadingTasks = useCompanyUploadTasks(companyId);
  const [visibleCount, setVisibleCount] = useState(IMAGE_RENDER_BATCH_SIZE);
  const { data: images = [], run: refresh } = useRequest(() => companyApi.listImages(companyId), {
    ready: !!companyId,
    refreshDeps: [companyId],
  });
  const visibleImages = useMemo(() => images.slice(0, visibleCount), [images, visibleCount]);
  const hasMoreImages = visibleCount < images.length;
  const loadMoreImages = () => {
    setVisibleCount((count) => Math.min(count + IMAGE_RENDER_BATCH_SIZE, images.length));
  };
  const handleImageListScroll = (event: UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (hasMoreImages && target.scrollHeight - target.scrollTop - target.clientHeight < 24) {
      loadMoreImages();
    }
  };

  useEffect(() => {
    if (!uploadingTasks.length) {
      refresh();
    }
  }, [refresh, uploadingTasks.length]);

  useEffect(() => {
    setVisibleCount(IMAGE_RENDER_BATCH_SIZE);
  }, [companyId]);

  if (!companyId) {
    return null;
  }

  return (
    <div className={styles.gallerySection}>
      <div className={styles.galleryMain}>
        <div className={styles.galleryTitle}>企业图片：</div>
        <div className={styles.imageScroller} onScroll={handleImageListScroll}>
          {images.length || uploadingTasks.length ? (
            <>
            <div className={styles.imageGrid}>
              {visibleImages.map((image) => (
                <ImageCard key={image.imageId} image={image} companyId={companyId} onChanged={refresh} />
              ))}
              {uploadingTasks.map((task) => (
                <div className={styles.imageCard} key={task.uid}>
                  <img src={task.previewUrl} alt={task.name} />
                  <div className={styles.uploadMask}>
                    <span>上传中 {task.percent}%</span>
                    <Progress percent={task.percent} size="small" showInfo={false} strokeColor="#ff7a1a" />
                  </div>
                </div>
              ))}
            </div>
            {hasMoreImages && (
              <Button block type="link" className={styles.loadMoreBtn} onClick={loadMoreImages}>
                查看更多
              </Button>
            )}
            </>
          ) : (
            <div className={styles.emptyText}>暂无图片</div>
          )}
        </div>
      </div>
      <AccessButton permission={['company:information:edit']}>
        <Button
          type="primary"
          onClick={() => {
            openCompanyImageModal({ companyId, images, refresh });
          }}
        >
          编辑图片
        </Button>
      </AccessButton>
    </div>
  );
};

export default CompanyImageGallery;
